import { useState, useMemo, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Revision, RevisionFile, RawApiRevision } from "shared/types/revision";
import { transformRevisions } from "./transformRevisions";
import { buildGallery } from "./buildGallery";
import ClientInspirationGrid from "./ClientInspirationGrid";
import {
  GetRevisionUploadsService,
  DownloadDesignerUploadService,
  DownloadRevisionInspirationService,
} from "shared/services/revision";
import { handleErrors, downloadBlobAsFile } from "shared/utils/helper";
import { filenameFromContentDisposition } from "shared/utils/contentDisposition";
import { toastMessage } from "shared/components/common/toast";
import { CustomAccordionGroup } from "shared/components/common/customAccordion";
import { Icons } from "assets";
import {
  uploadAcceptString,
  filesAllowedFor,
  CAD_NOT_ALLOWED_MESSAGE,
} from "shared/constants/officeFiles";
import classNames from "classnames";
import styles from "./style.module.scss";
import CustomButton from "shared/components/common/customButton";
import CustomBadge from "shared/components/common/customBadge";
import CustomGalleryView from "shared/components/common/customGalleryView";
import CustomPageHeading from "shared/components/common/customPageHeading";
import useWindowDimensions from "shared/hooks/useWindowDimensions";
import ChangeRequestModal from "shared/modal/changeRequestModal";
import ProceedPhaseModal from "shared/modal/proceedPhaseModal";
import ConfirmationModal from "shared/modal/confirmation";
import { startWorkingLabel } from "./startWorkingLabel";
import FilterDropdown from "shared/components/common/filterDropdown";
import NoContentCard from "shared/components/common/noContentCard";
import Spinner from "shared/components/common/spinner";

// Date / author / file-count stamp, shown once per comment block.
const MetaStamp = ({
  date,
  author,
  fileCount,
}: {
  date?: string;
  author?: string;
  fileCount?: number;
}) => (
  <div className={styles.metadata}>
    {date && (
      <div className={styles.metadataItem}>
        <Icons.CalendarIcon className={styles.metadataIcon} />
        <span>{date}</span>
      </div>
    )}
    {author && (
      <div className={styles.metadataItem}>
        <Icons.PersonIcon className={styles.metadataIcon} />
        <span>{author}</span>
      </div>
    )}
    <div className={styles.metadataItem}>
      <Icons.TabOverview className={styles.metadataIcon} />
      <span>{fileCount ?? 0} files</span>
    </div>
  </div>
);

interface RevisionsMediaProps {
  showBadge?: boolean;
  revisions?: Revision[] | RawApiRevision[];
  onDownloadPDF?: (revisionId: string) => void;
  onDownload3D?: (revisionId: string) => void;
  onFileClick?: (revision: Revision, fileId: string) => void;
  showActionButtons?: boolean;
  // When false, hides the "Proceed to <next phase>" button (view-only
  // collaborators).
  showProceedButton?: boolean;
  // When false, hides the "Request a Revision" button (view-only
  // collaborators).
  showRequestRevisionButton?: boolean;
  projectId?: number;
  projectName?: string;
  onProceedSuccess?: () => void;
  projectStatus?: string;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  editableRevisionId?: string;
  onEditRevision?: (revisionId: string) => void;
  // Inline editing for the editable revision only. When all three are
  // provided (and editableRevisionId matches), tiles become draggable to
  // reorder, gain a delete button, and a trailing "+" add card is shown.
  onReorderFiles?: (
    revisionId: string,
    orderedFileIds: string[],
  ) => Promise<void>;
  onDeleteFile?: (revisionId: string, fileId: string) => Promise<void>;
  onAddFiles?: (revisionId: string, files: File[]) => Promise<void>;
  // Designer-only: adds an upload action, above the list or in the empty state.
  onUploadRevision?: () => void;
  // Designer-only: picks up a revision the client sent back for changes.
  onStartWorking?: () => void;
  startWorkingBusy?: boolean;
  startWorkingDisabled?: boolean;
  // Blocks uploading until the designer has started work on the revision.
  uploadDisabled?: boolean;
  // Designer-only: persist a shared sort for a revision's files. Pass null for
  // Default (manual order). When provided (and viewer is the designer), each
  // revision card shows a sort dropdown.
  onChangeSort?: (
    revisionId: string,
    mediaSort:
      | "name_asc"
      | "name_desc"
      | "modified_asc"
      | "modified_desc"
      | null,
  ) => Promise<void>;
}

const RevisionsMedia = ({
  showBadge = true,
  revisions: revisionsProp,
  onDownloadPDF: onDownloadPDFProp,
  onDownload3D: onDownload3DProp,
  onFileClick: onFileClickProp,
  showActionButtons = false,
  showProceedButton = true,
  showRequestRevisionButton = true,
  projectId,
  projectName,
  onProceedSuccess,
  projectStatus,
  hasMore,
  isLoadingMore,
  onLoadMore,
  editableRevisionId,
  onEditRevision,
  onReorderFiles,
  onDeleteFile,
  onAddFiles,
  onUploadRevision,
  onStartWorking,
  startWorkingBusy,
  startWorkingDisabled,
  uploadDisabled,
  onChangeSort,
}: RevisionsMediaProps) => {
  const { width } = useWindowDimensions();
  const auth = useSelector((state: any) => state.root.auth);
  const userType = auth?.user?.user_type;
  const shouldShowInternalComments =
    userType !== "client" &&
    userType !== "collaborator" &&
    userType !== "builder";
  const [downloadingPdfRevisionId, setDownloadingPdfRevisionId] = useState<
    string | null
  >(null);
  const [downloadingImagesRevisionId, setDownloadingImagesRevisionId] =
    useState<string | null>(null);
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(
    null,
  );
  // Tracked apart from downloadingFileId: the two id spaces can collide.
  const [downloadingClientFileId, setDownloadingClientFileId] = useState<
    string | null
  >(null);
  const isAtOrBeyondFinalReview = () => {
    if (!projectStatus) return false;
    const statusLower = projectStatus.toLowerCase().replace(/[_\s-]/g, "");
    return (
      statusLower.includes("finalreview") || statusLower.includes("complete")
    );
  };

  const phaseSteps = [
    { label: "Consultation" },
    { label: "Preliminary" },
    { label: "Preliminary Finals" },
    { label: "Final Review" },
    { label: "Complete" },
  ];

  const getNextPhaseLabel = (): string => {
    if (!projectStatus) return phaseSteps[1]?.label ?? "Next Phase";
    const statusLower = projectStatus.toLowerCase().replace(/[_\s-]/g, "");
    let currentIndex = 0;
    if (statusLower.includes("consultation")) currentIndex = 0;
    else if (statusLower.includes("preliminaryfinal")) currentIndex = 2;
    else if (statusLower.includes("preliminary")) currentIndex = 1;
    else if (
      statusLower.includes("finalreview") ||
      statusLower.includes("final")
    )
      currentIndex = 3;
    else if (statusLower.includes("complete")) currentIndex = 4;
    const nextIndex = Math.min(currentIndex + 1, phaseSteps.length - 1);
    return phaseSteps[nextIndex]?.label ?? "Next Phase";
  };

  const canRequestChanges = (rawStatus?: string) => {
    if (!rawStatus) return false;
    const statusLower = rawStatus.toLowerCase().replace(/[_\s-]/g, "");
    return statusLower.includes("waitingforclientresponse");
  };

  const isRevisionCompleted = (rawStatus?: string) => {
    if (!rawStatus) return false;
    const statusLower = rawStatus.toLowerCase().replace(/[_\s-]/g, "");
    return statusLower.includes("completed");
  };

  const revisions = useMemo(() => {
    if (!revisionsProp || revisionsProp.length === 0) return [];

    const firstItem = revisionsProp[0] as any;
    const isRawApiData =
      firstItem.revision_id !== undefined ||
      firstItem.designer_uploads !== undefined ||
      (firstItem.designer && firstItem.designer_comment !== undefined);

    if (isRawApiData) {
      return transformRevisions(revisionsProp as RawApiRevision[]);
    }

    return revisionsProp as Revision[];
  }, [revisionsProp]);

  // ---- Inline editing (editable revision only) ----
  const inlineEditEnabled = !!(
    editableRevisionId &&
    onReorderFiles &&
    onDeleteFile &&
    onAddFiles
  );
  // Canonical files of the editable revision, straight from props.
  const editableFilesFromProps = useMemo(
    () => revisions.find((r) => r.id === editableRevisionId)?.files ?? null,
    [revisions, editableRevisionId],
  );
  // Working copy used for optimistic reorder/delete; resynced whenever the
  // canonical data changes (e.g. after a parent refresh).
  const [inlineFiles, setInlineFiles] = useState<RevisionFile[] | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [deletingFileId, setDeletingFileId] = useState<string | null>(null);
  const [addingFiles, setAddingFiles] = useState(false);
  const inlineAddInputRef = useRef<HTMLInputElement>(null);
  const sectionContainerRef = useRef<HTMLDivElement>(null);
  const scrollEndTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Add a `scrolling` class while the strip is actively scrolling and remove
  // it shortly after scrolling stops. The SCSS uses it to disable tile
  // pointer-events during scroll so a stationary cursor doesn't retrigger
  // :hover on each tile passing under it (which made the hover lift bob).
  const handleSectionScroll = () => {
    const el = sectionContainerRef.current;
    if (el) {
      el.classList.add(styles.scrolling);
      if (scrollEndTimerRef.current) clearTimeout(scrollEndTimerRef.current);
      scrollEndTimerRef.current = setTimeout(() => {
        el.classList.remove(styles.scrolling);
      }, 150);
    }
  };

  useEffect(() => {
    return () => {
      if (scrollEndTimerRef.current) clearTimeout(scrollEndTimerRef.current);
    };
  }, []);
  const [pendingDelete, setPendingDelete] = useState<{
    revisionId: string;
    fileId: string;
  } | null>(null);

  useEffect(() => {
    setInlineFiles(editableFilesFromProps);
  }, [editableFilesFromProps]);

  const resyncInlineFiles = () => setInlineFiles(editableFilesFromProps);

  const handleInlineDragStart = (
    e: React.DragEvent<HTMLDivElement>,
    index: number,
  ) => {
    e.dataTransfer.setData("text/plain", String(index));
    e.dataTransfer.effectAllowed = "move";
    // Defer so re-rendering the dragged tile during dragstart doesn't abort it.
    window.setTimeout(() => setDraggedIndex(index), 0);
  };

  const handleInlineDragEnter = (index: number) => {
    if (draggedIndex === null || draggedIndex === index) return;
    setInlineFiles((prev) => {
      if (!prev) return prev;
      const next = [...prev];
      const [moved] = next.splice(draggedIndex, 1);
      next.splice(index, 0, moved);
      return next;
    });
    setDraggedIndex(index);
  };

  const handleInlineDrop = async (revisionId: string) => {
    setDraggedIndex(null);
    if (!inlineFiles || !onReorderFiles) return;
    // Skip the save if order is unchanged.
    const before = editableFilesFromProps?.map((f) => f.id).join(",");
    const after = inlineFiles.map((f) => f.id).join(",");
    if (before === after) return;
    try {
      await onReorderFiles(
        revisionId,
        inlineFiles.map((f) => f.id),
      );
    } catch {
      resyncInlineFiles();
    }
  };

  // Open the in-app confirmation dialog; the actual delete runs on confirm.
  const requestInlineDelete = (revisionId: string, fileId: string) => {
    setPendingDelete({ revisionId, fileId });
  };

  const confirmInlineDelete = async () => {
    if (!pendingDelete || !onDeleteFile) return;
    const { revisionId, fileId } = pendingDelete;
    setDeletingFileId(fileId);
    setInlineFiles((prev) =>
      prev ? prev.filter((f) => f.id !== fileId) : prev,
    );
    try {
      await onDeleteFile(revisionId, fileId);
    } catch {
      resyncInlineFiles();
    } finally {
      setDeletingFileId(null);
      setPendingDelete(null);
    }
  };

  const handleInlineAddChange = async (
    revisionId: string,
    fileList: FileList | null,
    isFinalPlans: boolean,
  ) => {
    if (!fileList || fileList.length === 0 || !onAddFiles) return;
    const { allowed, rejected } = filesAllowedFor(
      Array.from(fileList),
      isFinalPlans,
    );
    if (rejected.length > 0) {
      toastMessage("error", CAD_NOT_ALLOWED_MESSAGE);
    }
    if (allowed.length === 0) return;
    setAddingFiles(true);
    try {
      await onAddFiles(revisionId, allowed);
    } finally {
      setAddingFiles(false);
    }
  };

  // Per-revision persisted sort (designer only). null = Default (manual order).
  const SORT_OPTIONS = [
    "Default Order",
    "Date Modified ↓",
    "Date Modified ↑",
    "Name A→Z",
    "Name Z→A",
  ];
  const sortValueToLabel = (value: Revision["mediaSort"]): string => {
    switch (value) {
      case "modified_desc":
        return "Date Modified ↓";
      case "modified_asc":
        return "Date Modified ↑";
      case "name_asc":
        return "Name A→Z";
      case "name_desc":
        return "Name Z→A";
      default:
        return "Default Order";
    }
  };
  const handleChangeSort = async (revisionId: string, label: string) => {
    if (!onChangeSort) return;
    const map: Record<string, Revision["mediaSort"]> = {
      "Default Order": null,
      "Date Modified ↓": "modified_desc",
      "Date Modified ↑": "modified_asc",
      "Name A→Z": "name_asc",
      "Name Z→A": "name_desc",
    };
    await onChangeSort(revisionId, map[label] ?? null);
  };

  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryItems, setGalleryItems] = useState<
    {
      id: string;
      src: string;
      name: string;
      alt?: string;
      thumbnail?: string;
    }[]
  >([]);
  const [galleryInitialIndex, setGalleryInitialIndex] = useState(0);
  const [changeRequestModal, setChangeRequestModal] = useState<boolean>(false);
  const [proceedPhaseModal, setProceedPhaseModal] = useState<boolean>(false);

  const openChangeRequestModal = () => {
    setChangeRequestModal(true);
  };

  const closeChangeRequestModal = () => {
    setChangeRequestModal(false);
  };

  const openProceedPhaseModal = () => {
    setProceedPhaseModal(true);
  };

  const closeProceedPhaseModal = () => {
    setProceedPhaseModal(false);
  };

  // Designer uploads and client inspirations are separate tables with separate
  // download routes, so each grid passes its own request and its own busy flag.
  const downloadFileWith = async (
    file: RevisionFile,
    request: typeof DownloadDesignerUploadService,
    setBusy: (fileId: string | null) => void,
  ) => {
    try {
      const fileId = parseInt(file.id);
      if (!fileId || isNaN(fileId)) {
        toastMessage("error", "Invalid file ID");
        return;
      }

      setBusy(file.id);
      const response = await request(fileId);

      if (response.status === 200) {
        downloadBlobAsFile(
          response.data,
          filenameFromContentDisposition(
            response.headers?.["content-disposition"],
          ) || file.name,
        );
        toastMessage("success", "File downloaded successfully");
      } else {
        toastMessage("error", "Failed to download file");
      }
    } catch (error: any) {
      handleErrors(error);
    } finally {
      setBusy(null);
    }
  };

  const handleDownloadFile = (file: RevisionFile) =>
    downloadFileWith(file, DownloadDesignerUploadService, setDownloadingFileId);

  const handleDownloadClientFile = (file: RevisionFile) =>
    downloadFileWith(
      file,
      DownloadRevisionInspirationService,
      setDownloadingClientFileId,
    );

  const getStatusBadge = (status?: "In Review" | "Approved" | "Rejected") => {
    switch (status) {
      case "In Review":
        return {
          bg: "#FDF6DD",
          textColor: "#AD7636",
        };
      case "Approved":
        return {
          bg: "#E7F7ED",
          textColor: "#088B3A",
        };
      case "Rejected":
        return {
          bg: "#FDE5DD",
          textColor: "#E0734F",
        };
      default:
        return {
          bg: "transparent",
          textColor: "#ED1C24",
        };
    }
  };

  const downloadRevisionFiles = async (
    revisionId: string,
    type: "pdf" | "images",
    fileLabel: string,
  ) => {
    try {
      const response = await GetRevisionUploadsService(
        parseInt(revisionId),
        type,
      );

      if (response.status === 200) {
        downloadBlobAsFile(
          response.data,
          filenameFromContentDisposition(
            response.headers?.["content-disposition"],
          ) || `revision-${revisionId}-${type}.zip`,
        );
        toastMessage("success", `${fileLabel} downloaded successfully`);
      } else {
        toastMessage("error", "Failed to download files");
      }
    } catch (error: any) {
      handleErrors(error);
    }
  };

  const handleDownloadPDF = async (revisionId: string) => {
    if (onDownloadPDFProp) {
      onDownloadPDFProp(revisionId);
      return;
    }

    if (!revisionId) {
      toastMessage("error", "Invalid revision ID");
      return;
    }

    try {
      setDownloadingPdfRevisionId(revisionId);
      await downloadRevisionFiles(revisionId, "pdf", "PDF files");
    } finally {
      setDownloadingPdfRevisionId(null);
    }
  };

  const handleDownload3D = async (revisionId: string) => {
    if (onDownload3DProp) {
      onDownload3DProp(revisionId);
      return;
    }

    if (!revisionId) {
      toastMessage("error", "Invalid revision ID");
      return;
    }

    try {
      setDownloadingImagesRevisionId(revisionId);
      await downloadRevisionFiles(revisionId, "images", "Image files");
    } finally {
      setDownloadingImagesRevisionId(null);
    }
  };

  const openGallery = (files: RevisionFile[], fileId: string) => {
    const gallery = buildGallery(files, fileId);
    if (!gallery) return;

    setGalleryItems(gallery.items);
    setGalleryInitialIndex(gallery.initialIndex);
    setGalleryOpen(true);
  };

  const handleFileClick = (revision: Revision, fileId: string) => {
    if (onFileClickProp) {
      onFileClickProp(revision, fileId);
      return;
    }

    openGallery(revision.files, fileId);
  };

  // The client's own images open in the same viewer, in their own gallery so
  // paging never crosses over into the designer's uploads.
  const handleClientFileClick = (revision: Revision, fileId: string) => {
    openGallery(revision.clientFiles, fileId);
  };

  const renderRevisionContent = (revision: Revision) => {
    const pdfFiles = revision.files.filter((f) => f.type === "pdf");
    const imageFiles = revision.files.filter((f) => f.type === "image");
    const statusBadge = getStatusBadge(revision.status);
    // The client stamp only exists once the client has actually responded.
    const hasClientFeedback = !!(
      revision.clientFeedbackDate || revision.clientFeedbackAuthor
    );

    // Inline editing is enabled only for the editable revision.
    const inlineEdit = inlineEditEnabled && editableRevisionId === revision.id;
    const filesToRender =
      inlineEdit && inlineFiles ? inlineFiles : revision.files;
    // A revision with no files still needs its Edit button — that is how files get added.
    const canEditThisRevision =
      !!onEditRevision && editableRevisionId === revision.id;
    // Per-revision sort control: designer only, and only when there are files.
    const showSortControl =
      !!onChangeSort && userType === "designer" && revision.files.length > 0;

    return (
      <div
        className={classNames(styles.revisionContent, "flex flex-col gap-4")}
      >
        <div className={styles.commentsSection}>
          <div className={styles.badgeRow}>
            <div className={styles.statusBadge}>
              <CustomBadge
                title={revision.badgeTitle}
                statusType={revision.status}
                bg={statusBadge.bg}
                textColor={statusBadge.textColor}
              />
            </div>
          </div>

          {(hasClientFeedback ||
            revision.commentsToClient ||
            revision.clientFiles.length > 0) && (
            <div className={styles.commentBlock}>
              <MetaStamp
                date={revision.clientFeedbackDate}
                author={revision.clientFeedbackAuthor}
                fileCount={revision.clientFileCount}
              />
              {revision.commentsToClient && (
                <CustomPageHeading
                  title="Comments from Client"
                  description={revision.commentsToClient}
                  size="medium"
                  renderHtml={true}
                />
              )}
              <ClientInspirationGrid
                files={revision.clientFiles}
                onImageClick={(fileId) =>
                  handleClientFileClick(revision, fileId)
                }
                onDownload={handleDownloadClientFile}
                downloadingFileId={downloadingClientFileId}
              />
            </div>
          )}

          {revision.clientChangesRequest && (
            <CustomPageHeading
              title="Client Changes Request on Revision C"
              description={revision.clientChangesRequest}
              size="medium"
              renderHtml={true}
            />
          )}

          <div className={styles.commentBlock}>
            <MetaStamp
              date={revision.date}
              author={revision.author}
              fileCount={revision.fileCount}
            />
            {(revision.commentsFromDesigner || showSortControl) && (
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  {revision.commentsFromDesigner && (
                    <CustomPageHeading
                      title="Comments from Designer"
                      description={revision.commentsFromDesigner}
                      size="medium"
                      renderHtml={true}
                    />
                  )}
                </div>
                {showSortControl && (
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={styles.sortLabel}>Sort:</span>
                    <FilterDropdown
                      label=""
                      options={SORT_OPTIONS}
                      selected={sortValueToLabel(revision.mediaSort)}
                      menuWidth="fit-content"
                      maxHeight="200px"
                      menuPosition="right"
                      onSelect={(value) => handleChangeSort(revision.id, value)}
                    />
                  </div>
                )}
              </div>
            )}
            {(filesToRender.length > 0 || inlineEdit) && (
              <div className={classNames(styles.filesGrid)}>
                {filesToRender.map((file, index) => (
                  <div
                    key={file.id}
                    draggable={inlineEdit}
                    onDragStart={
                      inlineEdit
                        ? (e) => handleInlineDragStart(e, index)
                        : undefined
                    }
                    onDragEnter={
                      inlineEdit
                        ? () => handleInlineDragEnter(index)
                        : undefined
                    }
                    onDragOver={
                      inlineEdit
                        ? (e) => {
                            e.preventDefault();
                            e.dataTransfer.dropEffect = "move";
                          }
                        : undefined
                    }
                    onDrop={
                      inlineEdit
                        ? (e) => {
                            // Only prevent the browser's default drop handling here.
                            // Persistence runs in onDragEnd (fires once on the source,
                            // even when released over grid padding) to avoid a double
                            // save/toast from both drop + dragend firing per drag.
                            e.preventDefault();
                          }
                        : undefined
                    }
                    onDragEnd={
                      inlineEdit
                        ? () => handleInlineDrop(revision.id)
                        : undefined
                    }
                    className={classNames(
                      styles.fileThumbnail,
                      "relative",
                      file.type === "image" && "cursor-pointer",
                    )}
                    style={
                      inlineEdit
                        ? {
                            cursor: "grab",
                            opacity: draggedIndex === index ? 0.4 : 1,
                          }
                        : undefined
                    }
                    onClick={() => {
                      if (file.type === "image") {
                        handleFileClick(revision, file.id);
                      }
                    }}
                  >
                    {file.type === "image" ? (
                      <div
                        className={classNames(
                          styles.imagePlaceholder,
                          "relative",
                        )}
                      >
                        <img
                          src={file.thumbnail}
                          alt={file.name}
                          draggable={false}
                          onError={(e) => {
                            // The source object may be missing (e.g. deleted from
                            // storage / 404). Swap the broken-image glyph for a
                            // neutral "image unavailable" placeholder icon.
                            const img = e.currentTarget;
                            if (img.dataset.fallback) return;
                            img.dataset.fallback = "true";
                            img.style.display = "none";
                            img.parentElement
                              ?.querySelector<HTMLElement>(
                                `.${styles.missingThumb}`,
                              )
                              ?.style.setProperty("display", "flex");
                          }}
                        />
                        <div
                          className={classNames(
                            styles.missingThumb,
                            "absolute inset-0 flex-col items-center justify-center gap-1",
                          )}
                          style={{ display: "none" }}
                        >
                          <Icons.TabImage className={styles.missingThumbIcon} />
                          <span>Unavailable</span>
                        </div>
                        {inlineEdit && (
                          <button
                            type="button"
                            aria-label="Delete file"
                            className={classNames(
                              styles.deleteIcon,
                              "absolute top-2 right-2 flex items-center justify-center",
                            )}
                            onClick={(e) => {
                              e.stopPropagation();
                              requestInlineDelete(revision.id, file.id);
                            }}
                            disabled={deletingFileId === file.id}
                          >
                            {deletingFileId === file.id ? (
                              <Spinner />
                            ) : (
                              <Icons.TrashOutline2 width={15} height={15} />
                            )}
                          </button>
                        )}
                        <button
                          type="button"
                          className={classNames(
                            styles.downloadIcon,
                            "absolute bottom-2 right-2 flex items-center justify-center",
                          )}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadFile(file);
                          }}
                          disabled={downloadingFileId === file.id}
                        >
                          {downloadingFileId === file.id ? (
                            <div className="flex items-center justify-center">
                              <Spinner />
                            </div>
                          ) : (
                            <Icons.Download />
                          )}
                        </button>
                      </div>
                    ) : (
                      <div
                        className={classNames(
                          styles.pdfPlaceholder,
                          "relative",
                        )}
                      >
                        {file.type === "pdf" ? (
                          <Icons.PDFIcon className={styles.thumbnailIcon} />
                        ) : (
                          <Icons.DocumentIcon
                            className={styles.thumbnailIcon}
                          />
                        )}
                        {inlineEdit && (
                          <button
                            type="button"
                            aria-label="Delete file"
                            className={classNames(
                              styles.deleteIcon,
                              "absolute top-2 right-2 flex items-center justify-center",
                            )}
                            onClick={(e) => {
                              e.stopPropagation();
                              requestInlineDelete(revision.id, file.id);
                            }}
                            disabled={deletingFileId === file.id}
                          >
                            {deletingFileId === file.id ? (
                              <Spinner />
                            ) : (
                              <Icons.TrashOutline2 width={15} height={15} />
                            )}
                          </button>
                        )}
                        <button
                          type="button"
                          className={classNames(
                            styles.downloadIcon,
                            "absolute bottom-2 right-2",
                          )}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadFile(file);
                          }}
                          disabled={downloadingFileId === file.id}
                        >
                          {downloadingFileId === file.id ? (
                            <div className="scale-50">
                              <Spinner />
                            </div>
                          ) : (
                            <Icons.Download />
                          )}
                        </button>
                      </div>
                    )}
                    <span className={styles.fileName}>{file.name}</span>
                  </div>
                ))}

                {inlineEdit && (
                  <div
                    className={classNames(styles.fileThumbnail, "relative")}
                    onClick={() => inlineAddInputRef.current?.click()}
                    role="button"
                    aria-label="Add file"
                  >
                    <div className={styles.addFileBox}>
                      {addingFiles ? (
                        <Spinner />
                      ) : (
                        <span className={styles.addFilePlus}>+</span>
                      )}
                    </div>
                    <span className={styles.fileName}>
                      {addingFiles ? "Uploading..." : "Add file"}
                    </span>
                    <input
                      ref={inlineAddInputRef}
                      type="file"
                      multiple
                      accept={uploadAcceptString(!!revision.isFinalPlans)}
                      style={{ display: "none" }}
                      onChange={(e) => {
                        handleInlineAddChange(
                          revision.id,
                          e.target.files,
                          !!revision.isFinalPlans,
                        );
                        e.target.value = "";
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {revision.internalComments && shouldShowInternalComments && (
            <CustomPageHeading
              title="Internal Comments (TCH Team)"
              description={revision.internalComments}
              size="medium"
              renderHtml={true}
            />
          )}
        </div>

        {(revision.files.length > 0 || canEditThisRevision) && (
          <div className={classNames(styles.downloadButtons, "flex gap-3")}>
            {pdfFiles.length > 0 && (
              <CustomButton
                title={
                  downloadingPdfRevisionId === revision.id
                    ? "Downloading..."
                    : "Download PDF"
                }
                Icon={Icons.Download}
                IconDirection={width < 640 ? "" : "left"}
                onClick={() => handleDownloadPDF(revision.id)}
                containerStyle={styles.downloadButton}
                disabled={downloadingPdfRevisionId === revision.id}
              />
            )}
            {imageFiles.length > 0 && (
              <CustomButton
                title={
                  downloadingImagesRevisionId === revision.id
                    ? "Downloading..."
                    : "Download Images"
                }
                Icon={Icons.Download}
                IconDirection={width < 640 ? "" : "left"}
                onClick={() => handleDownload3D(revision.id)}
                containerStyle={styles.downloadButton}
                disabled={downloadingImagesRevisionId === revision.id}
              />
            )}
            {canEditThisRevision && (
              <CustomButton
                title="Edit Revision"
                Icon={Icons.FileUploadGlyph}
                IconDirection={width < 640 ? "" : "left"}
                onClick={() => onEditRevision?.(revision.id)}
                containerStyle={styles.downloadButton}
              />
            )}
          </div>
        )}
      </div>
    );
  };

  // Client / collaborator actions ride in the revision header so they sit at
  // the top of the card instead of below the files.
  const renderRevisionActions = (revision: Revision) => {
    if (
      !showActionButtons ||
      !canRequestChanges(revision.badgeTitle) ||
      isAtOrBeyondFinalReview()
    )
      return null;

    const canProceed =
      showProceedButton && !isRevisionCompleted(revision.status);
    if (!showRequestRevisionButton && !canProceed) return null;

    return (
      <>
        {showRequestRevisionButton && (
          <CustomButton
            title="Request a Revision"
            containerStyle={styles.buttonRequest}
            titleStyle={styles.title}
            Icon={Icons.EditIcon}
            iconStyle={styles.icon}
            IconDirection="left"
            type="button"
            onClick={openChangeRequestModal}
          />
        )}

        {canProceed && (
          <CustomButton
            title={`Proceed to ${getNextPhaseLabel()}`}
            containerStyle={styles.buttonPhase}
            titleStyle={styles.title}
            Icon={Icons.FileAcceptIcon}
            iconStyle={styles.icon}
            IconDirection="left"
            type="button"
            onClick={openProceedPhaseModal}
          />
        )}
      </>
    );
  };

  const accordionItems = revisions
    ? revisions.map((revision) => ({
        id: revision.id,
        title: revision.title,
        badgeTitle: revision.isFinalPlans
          ? "Final Plans"
          : revision.id === "4"
            ? "New"
            : undefined,
        headerActions: renderRevisionActions(revision),
        content: renderRevisionContent(revision),
      }))
    : [];

  return (
    <>
      {(onUploadRevision || onStartWorking) && accordionItems.length > 0 && (
        <div className="flex justify-end items-center gap-3 flex-wrap mt-3">
          {onStartWorking && (
            <CustomButton
              title={startWorkingLabel({
                busy: !!startWorkingBusy,
                disabled: startWorkingDisabled,
              })}
              Icon={Icons.Rocket}
              IconDirection={width < 640 ? "" : "left"}
              type="button"
              containerStyle="primary-btn"
              onClick={onStartWorking}
              disabled={startWorkingBusy || startWorkingDisabled}
            />
          )}
          {onUploadRevision && (
            <CustomButton
              title="Upload Revision"
              Icon={Icons.UploadRevisionIcon}
              IconDirection={width < 640 ? "" : "left"}
              type="button"
              containerStyle="primary-btn"
              onClick={onUploadRevision}
              disabled={uploadDisabled}
            />
          )}
        </div>
      )}
      <div
        ref={sectionContainerRef}
        className={classNames(
          styles.sectionContainer,
          "flex flex-col md:gap-5 gap-4 mt-3",
        )}
        onScroll={(e) => {
          handleSectionScroll();
          if (!onLoadMore || !hasMore || isLoadingMore) return;
          const target = e.currentTarget;
          const bottom =
            target.scrollHeight - target.scrollTop <= target.clientHeight + 50;
          if (bottom) onLoadMore();
        }}
      >
        {accordionItems.length > 0 ? (
          <>
            <CustomAccordionGroup
              items={accordionItems}
              defaultOpenIndex={0}
              showBadge={showBadge}
            />
            {isLoadingMore && (
              <div className="flex items-center justify-center py-8">
                <Spinner />
              </div>
            )}
          </>
        ) : (
          <div
            className={classNames(
              styles.noContentCardContainer,
              "flex flex-col items-center justify-center gap-5",
            )}
          >
            <NoContentCard
              Icon={Icons.NoRevisions}
              customIconContianer={classNames(styles.noContentCardIcon)}
              label1="No media yet"
              label2={
                onUploadRevision
                  ? "Upload your first revision to share it with the client."
                  : ""
              }
            />
            {onUploadRevision && (
              <CustomButton
                title="Upload First Revision"
                Icon={Icons.UploadRevisionIcon}
                IconDirection="left"
                type="button"
                containerStyle="primary-btn"
                onClick={onUploadRevision}
                disabled={uploadDisabled}
              />
            )}
          </div>
        )}
      </div>

      <CustomGalleryView
        show={galleryOpen}
        handleClose={() => setGalleryOpen(false)}
        items={galleryItems}
        initialIndex={galleryInitialIndex}
      />

      <ChangeRequestModal
        title={"Revision Request"}
        description={
          [
            projectName,
            revisionsProp && revisionsProp.length > 0
              ? (revisionsProp[0] as any).title ||
                (revisionsProp[0] as any).revision_title
              : undefined,
          ]
            .filter(Boolean)
            .join(" - ") || ""
        }
        icon={Icons.EditIcon2}
        iconContainerStyle={styles.iconContainerEdit}
        isOpen={changeRequestModal}
        onClose={closeChangeRequestModal}
        revisionId={
          revisionsProp && revisionsProp.length > 0
            ? Number(
                (revisionsProp[0] as any).revision_id ||
                  (revisionsProp[0] as any).id,
              )
            : undefined
        }
        onSuccess={onProceedSuccess}
      />
      <ProceedPhaseModal
        icon={Icons.CheckMarkCircleIcon2}
        iconContainerStyle={styles.iconContainerMark}
        iconStyle={styles.iconMark}
        isOpen={proceedPhaseModal}
        onClose={closeProceedPhaseModal}
        onRequestRevision={() => {
          closeProceedPhaseModal();
          openChangeRequestModal();
        }}
        onSuccess={() => {
          onProceedSuccess?.();
        }}
        forceReload={true}
        projectId={projectId}
        revisions={revisionsProp}
        currentStatus={projectStatus}
      />

      <ConfirmationModal
        show={!!pendingDelete}
        handleClose={() => setPendingDelete(null)}
        handleSubmit={confirmInlineDelete}
        title="Delete File"
        subtitle="Are you sure you want to delete this file? This action cannot be undone."
        actionButtonText="Delete"
        cancelButtonText="Cancel"
        modalType="delete"
        loading={!!deletingFileId}
      />
    </>
  );
};

export default RevisionsMedia;
